import React, { useRef, useState, useEffect } from 'react';
import { Camera, Plus, Loader2 } from 'lucide-react';
import { DEFAULT_AVATAR_PATH } from '../../../utils/avatar';

const ProfileSection = ({
    isMobile,
    // ── renamed: displayName → fullName ──────────────────────────────────────
    fullName,
    setFullName,
    phone,
    setPhone,
    title,
    setTitle,
    license,
    setLicense,
    companyName,
    setCompanyName,
    // ── new photo props ───────────────────────────────────────────────────────
    photoURL,           // current URL from Firestore (empty string if none)
    onPhotoChange,      // (File) => void — called when user picks a new photo
    onSave,
    saving
}) => {
    const photoInputRef = useRef(null);
    const [imgBroken, setImgBroken] = useState(false);
    const [localPreview, setLocalPreview] = useState(null);

    // Reset broken state whenever a new photoURL is provided (e.g. after upload)
    useEffect(() => { setImgBroken(false); }, [photoURL]);

    // Clean up object URL on unmount or when preview changes
    useEffect(() => {
        return () => { if (localPreview) URL.revokeObjectURL(localPreview); };
    }, [localPreview]);

    const handlePhotoClick = () => photoInputRef.current?.click();

    const handlePhotoFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (onPhotoChange) onPhotoChange(file);
        // Show instant local preview (not saved until Save Changes is clicked)
        if (localPreview) URL.revokeObjectURL(localPreview);
        setLocalPreview(URL.createObjectURL(file));
        setImgBroken(false);
        // Reset input so the same file can be re-selected if needed
        e.target.value = '';
    };

    const displayPhotoURL = imgBroken
        ? DEFAULT_AVATAR_PATH
        : (localPreview || photoURL || DEFAULT_AVATAR_PATH);

    // Shared photo circle UI used by both layouts
    const PhotoCircle = ({ size = 'w-24 h-24', iconSize = 'w-6 h-6' }) => (
        <div
            className={`${size} relative rounded-full flex items-center justify-center border border-dashed border-stone-300 hover:border-stone-500 cursor-pointer transition overflow-hidden bg-stone-100`}
            onClick={handlePhotoClick}
            title="Click to change photo"
        >
            <img
                src={displayPhotoURL}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={() => {
                    if (!imgBroken) setImgBroken(true);
                }}
            />
            {!localPreview && !photoURL && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/0">
                    <Camera className={`${iconSize} text-stone-400`} />
                </div>
            )}
        </div>
    );

    return (
        <form onSubmit={(e) => e.preventDefault()}>
            {/* Hidden file input */}
            <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoFile}
            />

            <div className="flex items-center justify-between mb-4 md:mb-6">
                <div>
                    <h2 className="text-lg font-bold text-notion-text">Profile &amp; Branding</h2>
                    <p className="text-xs text-stone-500">{isMobile ? 'Manage your public identity.' : 'Manage your public identity across websites and emails.'}</p>
                </div>
                <button
                    onClick={onSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-stone-800 text-white rounded text-xs font-medium hover:bg-stone-700 disabled:bg-stone-400 disabled:cursor-not-allowed transition"
                >
                    {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            <div className="bg-white border border-notion-border rounded-xl p-5 md:p-6 shadow-card mb-4 md:mb-6">
                {isMobile ? (
                    // ── Mobile layout ──────────────────────────────────────────────────────
                    <>
                        <div className="flex items-center gap-4 mb-5">
                            <div className="relative">
                                <PhotoCircle size="w-16 h-16" iconSize="w-5 h-5" />
                                <div className="absolute bottom-0 right-0 w-5 h-5 bg-stone-800 rounded-full flex items-center justify-center border-2 border-white pointer-events-none">
                                    <Plus className="w-3 h-3 text-white" />
                                </div>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-notion-text">Profile Photo</h3>
                                <button
                                    type="button"
                                    onClick={handlePhotoClick}
                                    className="text-xs text-stone-500 underline decoration-stone-300"
                                >
                                    Tap to change
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {/* ── Full Name (was Display Name) ──────── */}
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Full Name</label>
                                <input
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2.5 text-sm outline-none border focus:border-stone-300 transition"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Company Name</label>
                                <input
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2.5 text-sm outline-none border focus:border-stone-300 transition"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Phone Number</label>
                                <input
                                    type="text"
                                    value={phone}
                                    placeholder="+1 (555) 000-0000"
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2.5 text-sm outline-none border focus:border-stone-300 transition"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Role</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2.5 text-sm outline-none border focus:border-stone-300 transition"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">License / DRE #</label>
                                <input
                                    type="text"
                                    value={license}
                                    onChange={(e) => setLicense(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2.5 text-sm outline-none border focus:border-stone-300 transition"
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    // ── Desktop layout ─────────────────────────────────────────────────────
                    <div className="flex gap-6">
                        <div className="flex-shrink-0 text-center">
                            <PhotoCircle size="w-24 h-24" iconSize="w-6 h-6" />
                            <button
                                type="button"
                                onClick={handlePhotoClick}
                                className="text-xs text-stone-500 hover:text-stone-800 mt-2 block w-full"
                            >
                                Change
                            </button>
                        </div>
                        <div className="flex-1 grid grid-cols-2 gap-4">
                            {/* ── Full Name (was Display Name) ──────── */}
                            <div className="col-span-2">
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Full Name</label>
                                <input
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Company Name</label>
                                <input
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Phone Number</label>
                                <input
                                    type="text"
                                    value={phone}
                                    placeholder="+1 (555) 000-0000"
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">Role</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-[10px] font-medium text-stone-500 mb-1">License / DRE #</label>
                                <input
                                    type="text"
                                    value={license}
                                    onChange={(e) => setLicense(e.target.value)}
                                    className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </form>
    );
};

export default ProfileSection;
